import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Trash2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const FIELD_LABELS = {
  clock_in: "Clock In",
  lunch_out: "Lunch Out",
  lunch_in: "Lunch In",
  clock_out: "Clock Out",
};

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatIsoLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

// Pay week is Thursday → Wednesday. Returns ISO date string (YYYY-MM-DD) of the Thursday that starts the week containing `dateStr`.
function payWeekStart(dateStr) {
  // Parse YYYY-MM-DD as local date (avoid UTC shift)
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay(); // Sun=0 ... Thu=4 ... Sat=6
  // Days to subtract to land on Thursday (4)
  const back = (dow - 4 + 7) % 7;
  dt.setDate(dt.getDate() - back);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function formatRange(start) {
  const end = addDays(start, 6);
  const fmt = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString([], { month: "short", day: "numeric" });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function todayDate() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const EMPTY_FORM = {
  user_id: "",
  date: todayDate(),
  clock_in: "",
  lunch_out: "",
  lunch_in: "",
  clock_out: "",
};

export default function Timecards() {
  const { user } = useAuth();
  const isAdminOrFull = user && (user.role === "admin" || user.role === "full");
  const isAdmin = user && user.role === "admin";

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [tcRes, usersRes] = await Promise.all([
        api.get("/timecards"),
        isAdmin ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setItems(tcRes.data);
      setUsers(usersRes.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Derive a unique user list from timecards if /users is not accessible (full role)
  const userOptions = isAdmin
    ? users
    : Array.from(
        new Map(
          items.map((t) => [t.user_id, { id: t.user_id, name: t.user_name, phone: t.user_phone }])
        ).values()
      );

  const openCreate = () => {
    setCreateForm({ ...EMPTY_FORM, user_id: userOptions[0]?.id || "" });
    setCreating(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.user_id) {
      toast.error("Pick an employee");
      return;
    }
    if (!createForm.date) {
      toast.error("Pick a date");
      return;
    }
    try {
      await api.post("/timecards", {
        user_id: createForm.user_id,
        date: createForm.date,
        clock_in: localToIso(createForm.clock_in),
        lunch_out: localToIso(createForm.lunch_out),
        lunch_in: localToIso(createForm.lunch_in),
        clock_out: localToIso(createForm.clock_out),
      });
      toast.success("Timecard created");
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openEdit = (tc) => {
    setEditing(tc);
    setEditForm({
      date: tc.date,
      clock_in: formatIsoLocal(tc.clock_in),
      lunch_out: formatIsoLocal(tc.lunch_out),
      lunch_in: formatIsoLocal(tc.lunch_in),
      clock_out: formatIsoLocal(tc.clock_out),
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/timecards/${editing.id}`, {
        date: editForm.date,
        clock_in: localToIso(editForm.clock_in),
        lunch_out: localToIso(editForm.lunch_out),
        lunch_in: localToIso(editForm.lunch_in),
        clock_out: localToIso(editForm.clock_out),
      });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.user_name}'s timecard for ${t.date}?`)) return;
    try {
      await api.delete(`/timecards/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = items.filter((t) =>
    !filter || t.user_name.toLowerCase().includes(filter.toLowerCase()) || (t.user_phone || "").includes(filter)
  );

  const totalHours = filtered.reduce((s, t) => s + Number(t.total_hours || 0), 0);

  // Group filtered timecards into pay weeks (Thu→Wed), then by employee.
  // Sort weeks descending (most recent first), employees alphabetically.
  const buildPayrollWeeks = (entries) => {
    const weeks = new Map(); // weekStart -> byUser Map
    for (const t of entries) {
      if (!t.date) continue;
      const ws = payWeekStart(t.date);
      if (!weeks.has(ws)) weeks.set(ws, new Map());
      const byUser = weeks.get(ws);
      const key = t.user_id;
      if (!byUser.has(key)) {
        byUser.set(key, { name: t.user_name, phone: t.user_phone, hours: 0, entries: [] });
      }
      const row = byUser.get(key);
      row.hours += Number(t.total_hours || 0);
      row.entries.push(t);
    }
    return Array.from(weeks.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([weekStart, byUser]) => {
        const rows = Array.from(byUser.values())
          .map((r) => ({
            ...r,
            entries: r.entries.slice().sort((a, b) => a.date.localeCompare(b.date)),
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return {
          weekStart,
          weekEnd: addDays(weekStart, 6),
          rows,
          total: rows.reduce((s, r) => s + r.hours, 0),
        };
      });
  };

  // Split for non-admin viewers who have visibility grants:
  //   My Timecard  = the logged-in user's own entries
  //   Team         = any other users' entries returned by the API
  const myEntries = isAdmin ? [] : filtered.filter((t) => t.user_id === user?.id);
  const teamEntries = isAdmin ? [] : filtered.filter((t) => t.user_id !== user?.id);
  const hasTeamSplit = !isAdmin && teamEntries.length > 0;

  const payrollWeeksAll = buildPayrollWeeks(filtered);
  const payrollWeeksMine = buildPayrollWeeks(myEntries);
  const payrollWeeksTeam = buildPayrollWeeks(teamEntries);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-7xl">
      <PageHeader
        eyebrow="Team"
        title="Timecards"
        description="All team timecards. Total worked hours are calculated automatically, excluding lunch."
        actions={
          isAdminOrFull ? (
            <Button
              onClick={openCreate}
              data-testid="tc-add-btn"
              className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Entry
            </Button>
          ) : null
        }
      />
      <div className="mt-6 flex items-center gap-3">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by user..."
          data-testid="tc-filter"
          className="flex-1 max-w-xs px-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
        />
        <div className="text-xs text-zinc-500 font-mono">{filtered.length} entries · {totalHours.toFixed(2)}h total</div>
      </div>

      {/* Weekly Payroll — entries grouped by pay week (Thursday → Wednesday) */}
      {hasTeamSplit ? (
        <>
          <PayrollSection
            heading="My Timecard"
            description="Your own clocked-in time. Pay week: Thursday → Wednesday."
            payrollWeeks={payrollWeeksMine}
            loading={loading}
            testId="payroll-section-mine"
            emptyLabel="No timecards yet."
            currentUserId={user?.id}
            isAdmin={isAdmin}
            isAdminOrFull={isAdminOrFull}
            openEdit={openEdit}
            remove={remove}
            accent="own"
          />
          <PayrollSection
            heading="Team Timecards"
            description="Employees you've been granted visibility to (view only)."
            payrollWeeks={payrollWeeksTeam}
            loading={loading}
            testId="payroll-section-team"
            emptyLabel="No team timecards to show."
            currentUserId={user?.id}
            isAdmin={isAdmin}
            isAdminOrFull={isAdminOrFull}
            openEdit={openEdit}
            remove={remove}
            accent="team"
          />
        </>
      ) : (
        <PayrollSection
          heading="Weekly Payroll"
          description="Pay week: Thursday → Wednesday. Entries roll up per employee."
          payrollWeeks={payrollWeeksAll}
          loading={loading}
          testId="payroll-section"
          emptyLabel="No timecards yet."
          currentUserId={user?.id}
          isAdmin={isAdmin}
          isAdminOrFull={isAdminOrFull}
          openEdit={openEdit}
          remove={remove}
          accent="all"
        />
      )}

      {/* Add Entry Dialog */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">New Timecard Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4" data-testid="tc-create-form">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Employee</Label>
              <select
                value={createForm.user_id}
                onChange={(e) => setCreateForm({ ...createForm, user_id: e.target.value })}
                data-testid="tc-create-user"
                className="w-full mt-1 px-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              >
                <option value="">Select employee...</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.phone}{u.phone ? ` · ${u.phone}` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Date</Label>
              <Input
                type="date"
                value={createForm.date}
                onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })}
                data-testid="tc-create-date"
                className="rounded-sm border-zinc-300 font-mono"
              />
            </div>
            {Object.keys(FIELD_LABELS).map((k) => (
              <div key={k}>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">
                  {FIELD_LABELS[k]}
                </Label>
                <Input
                  type="datetime-local"
                  value={createForm[k] || ""}
                  onChange={(e) => setCreateForm({ ...createForm, [k]: e.target.value })}
                  data-testid={`tc-create-${k}`}
                  className="rounded-sm border-zinc-300 font-mono"
                />
              </div>
            ))}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreating(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" data-testid="tc-create-save" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              Edit {editing?.user_name} · {editing?.date}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={submitEdit} className="space-y-4" data-testid="tc-edit-form">
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Date</Label>
                <Input
                  type="date"
                  value={editForm.date || ""}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  data-testid="tc-edit-date"
                  className="rounded-sm border-zinc-300 font-mono"
                />
              </div>
              {Object.keys(FIELD_LABELS).map((k) => (
                <div key={k}>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">
                    {FIELD_LABELS[k]}
                  </Label>
                  <Input
                    type="datetime-local"
                    value={editForm[k] || ""}
                    onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                    data-testid={`tc-edit-${k}`}
                    className="rounded-sm border-zinc-300 font-mono"
                  />
                </div>
              ))}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
                <Button type="submit" data-testid="tc-edit-save" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PayrollSection({
  heading,
  description,
  payrollWeeks,
  loading,
  testId,
  emptyLabel,
  currentUserId,
  isAdmin,
  isAdminOrFull,
  openEdit,
  remove,
  accent = "all",
}) {
  const headerClass =
    accent === "own"
      ? "bg-[#002FA7] text-white"
      : accent === "team"
      ? "bg-zinc-700 text-white"
      : "bg-zinc-950 text-white";
  const headerSubClass =
    accent === "own" ? "text-blue-100" : "text-zinc-400";

  return (
    <div className="mt-8" data-testid={testId}>
      <div className="flex items-end justify-between border-b border-zinc-200 pb-3">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-zinc-950">{heading}</h2>
          <div className="text-sm text-zinc-500 mt-1">{description}</div>
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
          {payrollWeeks.length} {payrollWeeks.length === 1 ? "week" : "weeks"}
        </div>
      </div>

      {loading && (
        <div className="mt-4 bg-white border border-zinc-200 rounded-sm px-4 py-8 text-center text-sm text-zinc-500">
          Loading...
        </div>
      )}
      {!loading && payrollWeeks.length === 0 && (
        <div className="mt-4 bg-white border border-zinc-200 rounded-sm px-4 py-8 text-center text-sm text-zinc-500">
          {emptyLabel}
        </div>
      )}

      <div className="mt-4 grid gap-6">
        {payrollWeeks.map((w) => (
          <div
            key={w.weekStart}
            className="bg-white border border-zinc-200 rounded-sm overflow-hidden"
            data-testid={`${testId}-week-${w.weekStart}`}
          >
            <div className={`flex items-center justify-between px-4 py-3 ${headerClass}`}>
              <div>
                <div className={`text-[10px] tracking-[0.3em] uppercase font-bold ${headerSubClass}`}>
                  Pay Week · Thu {w.weekStart}
                </div>
                <div className="font-mono font-bold text-white text-base mt-0.5">
                  {formatRange(w.weekStart)}
                </div>
              </div>
              <div className="text-right">
                <div className={`text-[10px] tracking-[0.3em] uppercase font-bold ${headerSubClass}`}>Week Total</div>
                <div
                  className="font-mono font-black text-white text-xl"
                  data-testid={`${testId}-week-total-${w.weekStart}`}
                >
                  {w.total.toFixed(2)}h
                </div>
              </div>
            </div>

            <div className="divide-y divide-zinc-200">
              {w.rows.map((r) => (
                <div
                  key={`${w.weekStart}-${r.name}-${r.phone}`}
                  data-testid={`${testId}-row-${w.weekStart}-${r.name}`}
                >
                  <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                    <div>
                      <div className="text-sm font-bold text-zinc-950">{r.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{r.phone || "—"}</div>
                    </div>
                    <div className="text-right flex items-center gap-6">
                      <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Days</div>
                        <div className="font-mono font-bold text-zinc-900 text-sm">{r.entries.length}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Hours</div>
                        <div
                          className="font-mono font-black text-zinc-950 text-base"
                          data-testid={`${testId}-employee-total-${w.weekStart}-${r.name}`}
                        >
                          {r.hours.toFixed(2)}h
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead className="bg-white border-b border-zinc-200">
                        <tr>
                          {["Date", "Clock In", "Lunch Out", "Lunch In", "Clock Out", "Hours", ""].map((h) => (
                            <th key={h} className="text-left px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {r.entries.map((t) => (
                          <tr key={t.id} className="hover:bg-zinc-50" data-testid={`tc-row-${t.id}`}>
                            <td className="px-4 py-3 text-sm font-mono font-semibold text-zinc-950">{t.date}</td>
                            <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.clock_in)}</td>
                            <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.lunch_out)}</td>
                            <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.lunch_in)}</td>
                            <td className="px-4 py-3 text-sm font-mono text-zinc-700">{formatTime(t.clock_out)}</td>
                            <td className="px-4 py-3 text-sm font-mono font-bold text-zinc-950">{t.total_hours.toFixed(2)}h</td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              {(isAdmin || (isAdminOrFull && t.user_id === currentUserId)) && (
                                <button
                                  onClick={() => openEdit(t)}
                                  data-testid={`edit-tc-${t.id}`}
                                  className="text-zinc-500 hover:text-zinc-950 mr-3"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                              {(isAdmin || (isAdminOrFull && t.user_id === currentUserId)) && (
                                <button
                                  onClick={() => remove(t)}
                                  data-testid={`delete-tc-${t.id}`}
                                  className="text-zinc-500 hover:text-[#FF2400]"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
